import { FC } from 'react'

import { useForm, Controller, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { variables } from 'src/Styled/variables'
import {
  AdCampaignStrategyEnum,
  AdCampaignVisualTypeEnum,
  AdVisualDescriptionQuestion,
} from 'src/Redux/ad-campaignApi'
import InfoIcon from 'src/Assets/Svg/common/info.svg?react'

import { AdsVisualModalButtons } from './AdsVisualModalButtons'
import { AdsVisualRadioItem } from './AdsVisualRadioItem'
import { useOptionalQuestions } from './useOptionalQuestions'

const TitleContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`

export const Title = styled.span`
  font-family: Segoe UI;
  color: ${({ theme }) => theme.color.darkGrey};
  font-size: ${variables.fontSize20};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > div {
    cursor: pointer;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;
`

const InfoMessageContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const InfoIconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const InfoText = styled.span`
  font-family: 'Segoe UI', sans-serif;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  color: ${({ theme }) => theme.color.darkGrey};
  line-height: 150%;
`

export interface VisualFormData {
  [key: string]: string | AdCampaignVisualTypeEnum | undefined
  visualType?: AdCampaignVisualTypeEnum
}

interface AdsVisualModalContentProps {
  onSaveVisualData: (formData: VisualFormData) => void
  visualQuestions?: AdVisualDescriptionQuestion[] | null
  strategyType?: AdCampaignStrategyEnum
  visualType?: AdCampaignVisualTypeEnum
}

export const AdsVisualModalContent: FC<AdsVisualModalContentProps> = ({
  onSaveVisualData,
  strategyType,
  visualQuestions,
  visualType,
}) => {
  const { setSearch } = useCustomSearchParams()
  const questionsSet = useOptionalQuestions(visualQuestions, strategyType)

  const defaultValues: VisualFormData = {
    ...(visualType ? { visualType } : {}),
    ...(questionsSet?.reduce(
      (acc, q) => {
        acc[q.fieldName] = q.answer
        return acc
      },
      {} as Record<string, string>,
    ) || {}),
  }

  const { control, handleSubmit } = useForm({
    defaultValues,
    mode: 'onChange',
  })

  const selectedVisualType = useWatch({ control, name: 'visualType' })
  const isVisualTypeSelected = Boolean(selectedVisualType)

  const onClickSaveVisual = (): void => {
    if (!isVisualTypeSelected) {
      return
    }
    handleSubmit((formData) => {
      const mappedData: VisualFormData = { visualType: formData.visualType }

      if (questionsSet) {
        questionsSet.forEach((q) => {
          if (formData[q.fieldName]) {
            mappedData[q.question] = formData[q.fieldName]
          }
        })
      }

      onSaveVisualData(mappedData)
    })()
  }

  const onClickGoBack = (): void => {
    setSearch({ modal: null })
  }

  return (
    <>
      <TitleContainer>
        <Title>Select ad visual type</Title>
      </TitleContainer>

      <ContentWrapper>
        <RadioWrapper>
          <Controller
            name='visualType'
            control={control}
            render={({ field }) => (
              <div onClick={() => field.onChange(AdCampaignVisualTypeEnum.AI)}>
                <AdsVisualRadioItem
                  checked={field.value === AdCampaignVisualTypeEnum.AI}
                  title={'AI-powered visuals'}
                  description={
                    'Get several professionally made AI videos tailored to your music'
                  }
                />
              </div>
            )}
          />

          <Controller
            name='visualType'
            control={control}
            render={({ field }) => (
              <div
                onClick={() => field.onChange(AdCampaignVisualTypeEnum.MANUAL)}
              >
                <AdsVisualRadioItem
                  checked={field.value === AdCampaignVisualTypeEnum.MANUAL}
                  title={'Let our experts create visuals'}
                  description={
                    'Receive several unique videos created by our professional team'
                  }
                />
              </div>
            )}
          />
        </RadioWrapper>

        {/* <OptionalQuestions*/}
        {/*  control={control}*/}
        {/*  visualQuestions={visualQuestions}*/}
        {/*  strategyType={strategyType}*/}
        {/* />*/}
      </ContentWrapper>

      <InfoMessageContainer>
        <InfoIconWrapper>
          <InfoIcon width={16} height={16} />
        </InfoIconWrapper>
        <InfoText>
          We will test multiple video ad creatives in the first few days, then
          automatically allocate budget to the top performing creative to
          maximize your results.
        </InfoText>
      </InfoMessageContainer>

      <AdsVisualModalButtons
        onClickSaveVisual={onClickSaveVisual}
        onClickGoBack={onClickGoBack}
        isVisualTypeSelected={isVisualTypeSelected}
      />
    </>
  )
}
